import { z } from 'zod'

import type { AdapterSchemaRelation } from '../../../engine/contracts'
import { getHostRuntime } from '../../../engine/host-runtime'
import {
  buildFilterLabelMaps,
  buildRelationReferenceLabels,
  savedFilterStructureSchema,
} from '../../filter-normalizer'
import { encodeFilterQuery } from '../../shared/filterQuery'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'

export type FilterPreviewAction =
  | { action: 'apply' }
  | { action: 'save' }
  | { action: 'refine' }
  | { action: 'cancel' }

// i18n key for the "save onto an existing segment" primary button. The card renders
// whatever key it receives, so the segment-specific wording lives here, not in the
// card. New-segment creation does NOT route through this tool — create_segment owns
// that flow and its own card.
const SAVE_TO_SEGMENT_LABEL = 'components.aiChatBot.filterPreview.saveToSegment'

export const showFilterPreviewTool = defineTool({
  name: 'show_filter_preview',
  description:
    'Show a filter preview card to the user with match count, conditions, and action buttons (Refine / Cancel plus a primary action). Always call this after build_filter — never present filter results as plain text. ' +
    'By default the primary button applies the filter to a list view. To refine an EXISTING segment, pass saveMode: "existingSegment" — the primary button then saves instead of applying, and the result is { saved: true } with no navigation. ' +
    'When saveMode is "existingSegment" and the result is { saved: true }: call save_filter_to_segment with the segment id. ' +
    'Never use this tool to CREATE a new segment — use the dedicated create_segment tool for that (it shows its own confirmation card). ' +
    'When the result contains { refine: true }: the user clicked Refine — ask them what they want to change ("What would you like to adjust?"), then wait for their reply and call build_filter again with the updated groups.',
  concurrencySafe: false,
  cacheable: false,
  inputSchema: () =>
    z.object({
      entityType: z.string().describe('Entity type being filtered'),
      structure: z
        .preprocess(
          (v) => (typeof v === 'string' ? JSON.parse(v) : v),
          savedFilterStructureSchema,
        )
        .describe('The SavedFilterStructure from build_filter'),
      matchCount: z
        .number()
        .nullable()
        .optional()
        .describe(
          'Total matching records from build_filter, or null when the count is unavailable (relation/reference filters that only PISA can evaluate on Apply)',
        ),
      description: z
        .string()
        .describe('Human-readable description of the filter'),
      saveMode: z
        .enum(['existingSegment'])
        .optional()
        .describe(
          'Set to "existingSegment" when refining an existing segment: the primary button saves the filter onto the segment instead of applying it to a list view. Omit for a plain listing filter. New-segment creation uses create_segment, not this tool.',
        ),
    }),

  async run(args, ctx, toolCallId) {
    if (!toolCallId) {
      return { error: 'show_filter_preview requires a toolCallId' }
    }

    const { data } = getHostRuntime()
    const structure = args.structure

    // Labels come from the schema, not from the model. Relaying them as tool args
    // meant the model had to copy a whole { code → label } map between calls; it
    // dropped entries, and the card rendered the unmapped codes verbatim.
    let fieldLabels: Record<string, string> = {}
    let enumLabels: Record<string, Record<string, string>> = {}
    try {
      const { schema, schemaAttributes } = await data.getSchema(
        data.getSchemaName(args.entityType),
      )
      const maps = buildFilterLabelMaps(schemaAttributes, structure.or)
      fieldLabels = maps.fieldLabels
      enumLabels = maps.enumLabels

      // Add readable labels for relation/reference conditions (record ids → names,
      // relation/nested titles), which the enum-only maps above don't cover.
      const rawRelations = schema?.get('relations')
      // schema.get() is typed `unknown`; the relations array shape is guaranteed by
      // the schema contract. Guarded by Array.isArray above.
      const relations = Array.isArray(rawRelations)
        ? (rawRelations as AdapterSchemaRelation[])
        : []
      const relMaps = await buildRelationReferenceLabels(
        schemaAttributes,
        relations,
        structure.or,
      )
      fieldLabels = { ...fieldLabels, ...relMaps.fieldLabels }
      enumLabels = { ...enumLabels, ...relMaps.valueLabels }
    } catch {
      // Schema unavailable — the card falls back to raw field keys and codes,
      // which is strictly better than failing the preview.
    }

    return new Promise<FilterPreviewAction>((resolve, reject) => {
      ctx.registerToolCallResolver?.(
        toolCallId,
        {
          type: 'filterPreview',
          toolCallId,
          entityType: args.entityType,
          structure,
          matchCount: args.matchCount ?? null,
          description: args.description,
          fieldLabels,
          enumLabels,
          ...(args.saveMode
            ? {
                primaryAction: 'save' as const,
                primaryLabel: SAVE_TO_SEGMENT_LABEL,
              }
            : {}),
        },
        // The resolver contract types the card result as `unknown`; the card only
        // ever resolves with a FilterPreviewAction discriminant.
        (result: unknown) => resolve(result as FilterPreviewAction),
        reject,
      )
    }).then((action) => {
      if (action.action === 'save') {
        // Do not navigate — return the canonical structure so the caller can
        // create the segment (create_entity) and/or attach it (save_filter_to_segment).
        return {
          saved: true,
          entityType: args.entityType,
          structure,
        }
      }

      if (action.action === 'apply') {
        const permalink = data.getOverviewPageLink(args.entityType)
        if (!permalink) {
          return { error: `No overview page for: ${args.entityType}` }
        }
        getHostRuntime().navigation.navigate({
          permalink,
          query: encodeFilterQuery(structure),
        })
        // Return the canonical structure so save_filter can use it verbatim
        // without reconstructing from context (which causes field/value drift).
        return {
          applied: true,
          entityType: args.entityType,
          structure,
        }
      }

      if (action.action === 'refine') {
        return {
          refine: true,
          previousStructure: structure,
          entityType: args.entityType,
        }
      }

      // cancel
      return { cancelled: true }
    })
  },
})
